import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth0 } from "@auth0/auth0-react";
import Box from "@mui/material/Box";
import { BodyLarge, H8TitleMedium } from "../../components/StyledComponents/Typography/Typography.tsx";
import { Controller, useForm } from "react-hook-form";
import {
  StyledFormControl,
  StyledSelect,
} from "../../components/StyledComponents/Inputs/Inputs.js";
import { StyledMenuItem } from "../../components/StyledComponents/Menu/Menu.tsx";
import CaseManagementSavedListForm from "./CaseManagementSavedListForm.js";
import List from "@mui/material/List";
import {
  Divider,
  ListItemButton,
  Skeleton,
  Typography,
} from "@mui/material";
import {
  PrimaryButton,
  SecondaryButton,
  StyledIconButton,
} from "../../components/StyledComponents/Buttons/AuradineButtons.js";
import CloseIcon from "@mui/icons-material/Close";
import { GetCaseFilterList } from "../../api/api.js";
import AuradineErrorState from "../../components/StyledComponents/EmptyStates/AuradineErrorState.tsx";
import { useTheme } from "../../ThemeContext/ThemeContext.js";


const SavedListsSkeleton = () => {
  const { colors } = useTheme();
  return (
    <Box sx={{ p: 3 }} height="60vh">
      <Box display="flex" gap={3}>
        <Box
          className="left-panel"
          display="flex"
          flexDirection="column"
          gap={3}
          p={2}
          sx={{ flex: 1 }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Skeleton
              variant="text"
              width={100}
              height={24}
            />
            <Skeleton
              variant="rectangular"
              width={140}
              height={36}
              sx={{ borderRadius: "6.25rem" }}
            />
          </Box>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={36}
          />
          <List disablePadding>
            <Skeleton
              variant="rectangular"
              height={36}
              sx={{ mb: 1, mt: 1 }}
            />
            <Divider />
            <Skeleton
              variant="rectangular"
              height={36}
              sx={{ mb: 1, mt: 1 }}
            />
            <Divider />
            <Skeleton
              variant="rectangular"
              height={36}
              sx={{ mb: 1, mt: 1 }}
            />
          </List>
        </Box>
      </Box>
    </Box>
  );
};

const SavedListFormSkeleton = () => {
  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Skeleton variant="text" width={180} height={24} />
        <Skeleton
          variant="circular"
          width={40}
          height={40}
        />
      </Box>
      <Box display="flex" flexDirection="column" gap={2}>
        {/* List Name Skeleton */}
        <Box display="flex" flexDirection="column" gap={1}>
          <Skeleton
            variant="text"
            sx={{ fontSize: "1rem" }}
            width="25%"
          />
          <Skeleton
            variant="rectangular"
            height={56}
            sx={{ borderRadius: 1 }}
          />
        </Box>

        {/* List Content Skeleton */}
        <Box display="flex" flexDirection="column" gap={1}>
          <Skeleton
            variant="text"
            sx={{ fontSize: "1rem" }}
            width="25%"
          />
          <Skeleton
            variant="rectangular"
            height={235}
            sx={{ borderRadius: 1 }}
          />
        </Box>

        {/* Submit Skeleton */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          pt={1}
        >
          <Skeleton
            variant="rectangular"
            width={120}
            height={40}
            sx={{ borderRadius: "2rem" }}
          />
          <Skeleton
            variant="rectangular"
            width={120}
            height={40}
            sx={{ borderRadius: "2rem" }}
          />
        </Box>
      </Box>
    </>
  );
};

const CaseManagementSavedLists = ({
  tabIndex,
  filterNames,
  refetchFilterNames,
  areFilterNamesLoading,
  getValue,
  setFilterValue,
}) => {
  const { colors } = useTheme();
  const [showForm, setShowForm] = React.useState(false);
  const [
    listItemButtonSelectedIndex,
    setListItemButtonSelectedIndex,
  ] = React.useState(null);
  const [selectedListName, setSelectedListName] =
    React.useState(null);
  const { getAccessTokenSilently, isAuthenticated } =
    useAuth0();

  const filterManagement = filterNames || {};

  const { control, watch } = useForm({
    defaultValues: { listName: "CaseNumbers" },
  });

  const selectedValue = watch("listName");

  const {
    isLoading: isSelectedListLoading,
    isError: isSelectedListError,
    error: selectedListError,
    data: selectedList,
  } = useQuery({
    queryKey: [
      "filterListContent",
      selectedValue,
      selectedListName,
    ],
    queryFn: async () => {
      if (!isAuthenticated || !selectedListName) {
        return null;
      }
      const token = await getAccessTokenSilently();
      const response = await GetCaseFilterList(
        {
          filterType: selectedValue,
          filterListName: selectedListName,
        },
        token
      );
      const listContent = {
        [selectedListName]: response.msg,
      };
      return listContent;
    },
    enabled: !!selectedListName,
    staleTime: 0,
    gcTime: 0,
  });

  const savedListOptions = [
    {
      label: "Case Numbers",
      value: "caseNo",
      placeholder:
        "Paste case numbers, one per line...",
    },
    {
      label: "Serial Numbers",
      value: "serialNo",
      placeholder: "Paste serial numbers, one per line...",
    },
  ];

  const selectedOption = savedListOptions.find(
    (o) => o.value === selectedValue
  );
  const listNames = filterManagement[selectedValue] || [];

  if (areFilterNamesLoading) {
    return <SavedListsSkeleton />;
  }

  return (
    <Box sx={{ p: 3 }} height="60vh">
      <Box display="flex" gap={showForm ? 3 : 0}>
        {/* Left panel */}
        <Box
          className="left-panel"
          display="flex"
          flexDirection="column"
          gap={3}
          p={2}
          sx={{
            flex: 1,
            minWidth: 0,
            contain: "layout",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <H8TitleMedium>Saved Lists</H8TitleMedium>
            <SecondaryButton colors={colors}
              disabled={listNames.length >= 2}
              startIcon={
                <span className="material-symbols-outlined">
                  add
                </span>
              }
              onClick={() => {
                setSelectedListName(null);
                setListItemButtonSelectedIndex(null);
                setShowForm(true);
              }}
            >
              Add new list
            </SecondaryButton>
          </Box>

          <StyledFormControl colors={colors} fullWidth>
            <Controller
              name="listName"
              control={control}
              render={({ field }) => (
                <StyledSelect
                  {...field}
                  colors={colors}
                  sx={{ minWidth: "15rem" }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 250,
                        overflowY: "auto",
                        backgroundColor: colors.background.paper,
                        color: colors.text.primary,
                      },
                    },
                  }}
                >
                  {savedListOptions.map((opt) => (
                    <StyledMenuItem
                      colors={colors}
                      key={opt.value}
                      value={opt.value}
                      onClick={() => {
                        setListItemButtonSelectedIndex(
                          null
                        );
                        setSelectedListName(null);
                        setShowForm(false);
                      }}
                    >
                      {opt.label}
                    </StyledMenuItem>
                  ))}
                </StyledSelect>
              )}
            />
          </StyledFormControl>

          {listNames.length > 0 ? (
            <List disablePadding>
              {listNames.map((name, index) => (
                <React.Fragment key={name}>
                  <ListItemButton
                    selected={
                      listItemButtonSelectedIndex === index
                    }
                    onClick={() => {
                      setShowForm(true);
                      setSelectedListName(name);
                      setListItemButtonSelectedIndex(index);
                    }}
                    sx={{
                      mb: 1,
                      mt: 1,
                      padding: "1rem",
                      borderRadius: "6.25rem",
                      "&:hover": {
                        backgroundColor:
                          colors.hover.muted,
                      },
                      "&.Mui-selected": {
                        backgroundColor: colors.background.accent,
                      },
                      "&.Mui-selected:hover": {
                        backgroundColor: colors.background.accent,
                      },
                    }}
                  >
                    <BodyLarge>{name}</BodyLarge>
                  </ListItemButton>
                  <Divider
                    flexItem
                    sx={{ backgroundColor: colors.border.default }}
                  />
                </React.Fragment>
              ))}
            </List>
          ) : (
            <H8TitleMedium>
              You don't have any saved lists. Click "Add new
              list" to get started.
            </H8TitleMedium>
          )}
        </Box>

        {/* Divider */}
        <Divider
          flexItem
          orientation="vertical"
          sx={{
            borderColor: colors.border.default,
            opacity: showForm ? 1 : 0,
            width: showForm ? "1px" : "0px",
            pointerEvents: showForm ? "auto" : "none",
          }}
        />

        {/* Right panel */}
        <Box
          className="right-panel"
          sx={{
            flex: showForm ? 1.5 : 0,
            minWidth: 0,
            overflow: "hidden",
            pointerEvents: showForm ? "auto" : "none",
            transition:
              "flex 0.1s ease-in-out, opacity 0.s ease-in-out",
            opacity: showForm ? 1 : 0,
          }}
        >
          <Box
            display="flex"
            flexDirection="column"
            gap={3}
            p={3}
            borderRadius={2}
            bgcolor={colors.background.highlight}
          >
            {isSelectedListLoading ? (
              <SavedListFormSkeleton />
            ) : isSelectedListError ? (
              <>
                <AuradineErrorState
                  message={
                    selectedListError.message ||
                    "An error occurred while fetching the list."
                  }
                />
              </>
            ) : (
              <>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <H8TitleMedium>
                    Add or view saved list
                  </H8TitleMedium>
                  <StyledIconButton
                    colors={colors}
                    onClick={() => {
                      setShowForm(false);
                      setSelectedListName(null);
                      setListItemButtonSelectedIndex(null);
                    }}
                  >
                    <CloseIcon />
                  </StyledIconButton>
                </Box>

                <CaseManagementSavedListForm
                  type={
                    selectedOption?.value
                      ? selectedOption?.value
                      : ""
                  }
                  placeholder={selectedOption?.placeholder}
                  refetchFilterNames={refetchFilterNames}
                  setFormOpen={setShowForm}
                  selectedList={selectedList || {}}
                  setListItemButtonSelectedIndex={
                    setListItemButtonSelectedIndex
                  }
                  getValue={getValue}
                  setFilterValue={setFilterValue}
                  setSelectedListName={setSelectedListName}
                />
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CaseManagementSavedLists;
